import { SuspensionNotice } from "../components/jm/SuspensionNotice";
import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  STEP_LABELS, DISCIPLINE_LABEL, StepIdentite, StepDojo, StepProfil, StepDisciplines, StepPoids, StepDispo,
  StepSante, StepReglements, StepAutorisations,
} from "../components/jm/v2/JmSteps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const localPricing = (n) => {
  const subtotal = n ? 30 + 10 * (n - 1) : 0;
  return { n, subtotal, total: Math.round(subtotal * 1.14975 * 100) / 100 };
};

export default function JmInscriptionPublic() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const [draft, setDraft] = useState(null);
  const [step, setStep] = useState(0);
  const [d, setD] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const token = sp.get("d") || localStorage.getItem(`jm_draft_${slug}`) || "";

  const hydrate = useCallback((r) => {
    setDraft(r); setD(r.data || {});
    setStep(r.status === "submitted" ? 10 : Math.min(r.step || 0, 9));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (token) {
          const r = await axios.get(`${API}/jm/public/draft/${token}`);
          hydrate(r.data); return;
        }
        const r = await axios.post(`${API}/jm/public/${slug}/draft`);
        localStorage.setItem(`jm_draft_${slug}`, r.data.token);
        setSp({ d: r.data.token }, { replace: true });
        hydrate(r.data);
      } catch (e) {
        if (token) { localStorage.removeItem(`jm_draft_${slug}`); setSp({}, { replace: true }); window.location.reload(); return; }
        setErr(e.response?.data?.detail || "Inscriptions fermées pour cet événement.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  const save = async (goNext = true) => {
    if (!draft) return;
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/jm/public/draft/${draft.token}`, { step, data: d });
      setDraft(r.data);
      if (goNext) { setStep(Math.min(step + 1, 9)); window.scrollTo({ top: 0, behavior: "smooth" }); }
      else toast.success("Brouillon enregistré");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };

  const upload = async (docType, file) => {
    const fd = new FormData(); fd.append("doc_type", docType); fd.append("file", file);
    try {
      await axios.post(`${API}/jm/public/draft/${draft.token}/documents`, fd);
      const r = await axios.get(`${API}/jm/public/draft/${draft.token}`); setDraft(r.data);
      toast.success("Document téléversé (confidentiel)");
    } catch (e) { toast.error(e.response?.data?.detail || "Téléversement impossible"); }
  };
  const delDoc = async (id) => {
    try { await axios.delete(`${API}/jm/public/draft/${draft.token}/documents/${id}`); const r = await axios.get(`${API}/jm/public/draft/${draft.token}`); setDraft(r.data); } catch { toast.error("Erreur"); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/jm/public/draft/${draft.token}`, { step: 8, data: d });
      const r = await axios.post(`${API}/jm/public/draft/${draft.token}/submit`);
      if (r.data.checkout_url) { window.location.href = r.data.checkout_url; return; }
      const fresh = await axios.get(`${API}/jm/public/draft/${draft.token}`); setDraft(fresh.data); setStep(10);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de soumission"); }
    setBusy(false);
  };

  if (err) return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6" data-testid="jm-v2-error">
      <div className="max-w-2xl w-full">{err.includes("suspendues") ? <SuspensionNotice /> : <div className="text-center">{err}</div>}</div>
    </div>
  );
  if (!draft) return <div className="min-h-screen bg-black text-white flex items-center justify-center">Chargement…</div>;

  const ev = draft.event; const pricing = draft.pricing; const rules = draft.rules || [];
  const stepEl = [
    <StepIdentite d={d} set={set} event={ev} />, <StepDojo d={d} set={set} />, <StepProfil d={d} set={set} />,
    <StepDisciplines d={d} set={set} event={ev} pricing={localPricing((d.entries || []).length)} />,
    <StepPoids d={d} set={set} />, <StepDispo d={d} set={set} />,
    <StepSante d={d} set={set} docs={draft.documents} onUpload={upload} onDeleteDoc={delDoc} />,
    <StepReglements d={d} set={set} rules={rules} />, <StepAutorisations d={d} set={set} event={ev} />,
    <PaymentStep d={d} pricing={pricing} docs={draft.documents} rules={rules} />, <ConfirmationStep draft={draft} d={d} />,
  ][step];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="jm-v2-page">
      <div className="max-w-2xl mx-auto px-5 py-8">
        <div className="mb-5">
          <div className="text-[10px] tracking-[0.35em] text-amber-400">JEUX MARTIAUX — INSCRIPTION GUIDÉE</div>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1" data-testid="jm-v2-title">{ev.name}</h1>
          <p className="text-zinc-400 text-sm">{ev.date} · Ton brouillon est sauvegardé à chaque étape — tu peux revenir plus tard avec ce lien. <a href="/politiques" target="_blank" rel="noreferrer" className="underline">Politiques</a></p>
        </div>
        <ol className="flex flex-wrap gap-1.5 mb-6" data-testid="jm-v2-stepper">
          {STEP_LABELS.map((l, i) => (
            <li key={l}>
              <button type="button" disabled={i > (draft.step ?? 0) || draft.status === "submitted"} onClick={() => setStep(i)} data-testid={`jm-v2-step-${i}`}
                className={`text-[11px] rounded-full px-2.5 py-1 border ${i === step ? "border-red-500 bg-red-500/15 text-white" : i <= (draft.step ?? 0) ? "border-white/15 text-zinc-300 hover:bg-white/5" : "border-white/5 text-zinc-600"}`}>
                {i + 1}. {l}
              </button>
            </li>
          ))}
        </ol>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <h2 className="text-lg font-semibold mb-4" data-testid="jm-v2-step-title">Étape {step + 1} / 11 — {STEP_LABELS[step]}</h2>
          {stepEl}
        </div>
        {step <= 8 && draft.status !== "submitted" && (
          <div className="flex gap-2 mt-4">
            <button type="button" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="rounded-xl border border-white/15 px-4 py-3 text-sm disabled:opacity-40" data-testid="jm-v2-prev">← Précédent</button>
            <button type="button" onClick={() => save(false)} disabled={busy} className="rounded-xl border border-white/15 px-4 py-3 text-sm" data-testid="jm-v2-save">💾 Enregistrer</button>
            <button type="button" onClick={() => save(true)} disabled={busy} className="flex-1 rounded-xl bg-red-600 hover:bg-red-500 py-3 font-semibold disabled:opacity-50" data-testid="jm-v2-next">{busy ? "…" : "Continuer →"}</button>
          </div>
        )}
        {step === 9 && draft.status !== "submitted" && (
          <div className="flex gap-2 mt-4">
            <button type="button" onClick={() => setStep(8)} className="rounded-xl border border-white/15 px-4 py-3 text-sm" data-testid="jm-v2-prev">← Précédent</button>
            <button type="button" onClick={submit} disabled={busy} className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-500 py-3 font-semibold disabled:opacity-50" data-testid="jm-v2-submit">{busy ? "…" : pricing.total > 0 ? `Confirmer et payer ${pricing.total.toFixed(2)} $` : "Confirmer l'inscription"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function PaymentStep({ d, pricing, docs, rules }) {
  const entries = d.entries || [];
  const missingRules = rules.filter((r) => !(d.accepted_rule_ids || []).includes(r.id));
  return (
    <div className="space-y-3 text-sm" data-testid="jm-v2-payment">
      <ul className="space-y-1">
        {entries.map((e, i) => <li key={e.discipline} className="flex justify-between"><span>{DISCIPLINE_LABEL[e.discipline] || e.discipline} · {e.belt_level}</span><b>{i === 0 ? "30,00 $" : "10,00 $"}</b></li>)}
      </ul>
      <div className="border-t border-white/10 pt-2 space-y-1 text-zinc-300">
        <div className="flex justify-between"><span>Sous-total</span><span>{pricing.subtotal.toFixed(2)} $</span></div>
        <div className="flex justify-between"><span>TPS</span><span>{pricing.tps.toFixed(2)} $</span></div>
        <div className="flex justify-between"><span>TVQ</span><span>{pricing.tvq.toFixed(2)} $</span></div>
        <div className="flex justify-between text-lg font-bold text-white"><span>Total</span><span data-testid="jm-v2-total">{pricing.total.toFixed(2)} $</span></div>
      </div>
      <p className="text-xs text-zinc-400">Paiement en ligne obligatoire (Stripe). L'inscription n'est confirmée qu'après paiement{entries.length >= 3 ? " et approbation de l'organisateur" : ""}.</p>
      <p className="text-xs text-zinc-400">{(docs || []).length} document(s) confidentiel(s) · {rules.length - missingRules.length}/{rules.length} règlement(s) accepté(s)</p>
      {missingRules.length > 0 && <p className="text-xs text-red-300" data-testid="jm-v2-missing-rules">⚠️ Règlements à accepter : {missingRules.map((r) => r.title).join(", ")}</p>}
    </div>
  );
}

function ConfirmationStep({ draft, d }) {
  return (
    <div className="space-y-3 text-sm" data-testid="jm-v2-confirmation">
      <div className="text-4xl">🎟️</div>
      <p><b>Inscription reçue.</b> Numéro : <code className="text-amber-300" data-testid="jm-v2-registration-id">{draft.registration_id}</code></p>
      <p>Athlète : {d.athlete_first_name} {d.athlete_last_name} · {(d.entries || []).map((e) => DISCIPLINE_LABEL[e.discipline] || e.discipline).join(", ")}</p>
      <p className="text-zinc-400">Catégories provisoires jusqu'à la pesée officielle. Un courriel de confirmation suit le paiement ; l'organisateur te contactera si un document manque.</p>
      {draft.checkout_url && <a href={draft.checkout_url} className="inline-block rounded-xl bg-emerald-600 px-4 py-2 font-semibold" data-testid="jm-v2-pay-link">Finaliser le paiement →</a>}
    </div>
  );
}
